/**
 * Zulassungsstellen Service
 * Loads and provides lookup for registration offices from CSV
 */

export interface Zulassungsstelle {
  id: number
  regionCode: string
  name: string
  office: string
  bundesland: string
  reservationFee: number
}

let cachedData: Zulassungsstelle[] | null = null

/**
 * Load and parse Zulassungstellen CSV from public directory
 */
export async function loadZulassungsstellen(): Promise<Zulassungsstelle[]> {
  if (cachedData) return cachedData

  const response = await fetch('/api/Zulassungstellen.csv')
  const text = await response.text()

  const lines = text.trim().split('\n')
  // Skip header
  const data: Zulassungsstelle[] = []

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue

    // Parse CSV with semicolons and quoted fields
    const parts = line.split(';').map(p => p.replace(/^"|"$/g, ''))
    if (parts.length < 6) continue

    data.push({
      id: parseInt(parts[0], 10),
      regionCode: parts[1],
      name: parts[2],
      office: parts[3],
      bundesland: parts[4],
      reservationFee: parseFloat(parts[5]) || 0,
    })
  }

  cachedData = data
  return data
}

/**
 * Find Zulassungsstellen matching a region code
 */
export async function findByRegionCode(regionCode: string): Promise<Zulassungsstelle[]> {
  const data = await loadZulassungsstellen()
  const upper = regionCode.toUpperCase()
  return data.filter(z => z.regionCode.toUpperCase() === upper)
}

/**
 * Search Zulassungsstellen by region code prefix (for autocomplete)
 */
export async function searchByPrefix(prefix: string): Promise<Zulassungsstelle[]> {
  const data = await loadZulassungsstellen()
  const upper = prefix.toUpperCase()
  return data.filter(z => z.regionCode.toUpperCase().startsWith(upper)).slice(0, 20)
}
